import React, { useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { 
  Menu,
  X,
  LayoutDashboard, 
  Building2, 
  Sparkles, 
  BarChart2, 
  Settings,
  Shield,
  ListTodo,
  LogOut
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStore } from '../../store/useStore';
import { NotificationsDropdown } from '../notifications/NotificationsDropdown';
import { clsx } from 'clsx';
import { isExecutive } from '../../utils/auth';
import { supabase } from '../../config/supabase';
import toast from 'react-hot-toast';

export const MobileNav: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const currentUser = useStore((state) => state.currentUser);
  const { setUser, clearAuth } = useStore();
  const location = useLocation();
  const navigate = useNavigate();

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', href: '/' },
    { icon: Building2, label: 'Departments', href: '/departments' },
    { icon: ListTodo, label: 'My Tasks', href: '/my-tasks' },
    ...(isExecutive(currentUser?.role || 'EMPLOYEE') ? [
      { icon: BarChart2, label: 'Analytics', href: '/analytics' },
      { icon: Shield, label: 'Admin Panel', href: '/admin' }
    ] : []),
    { icon: Settings, label: 'Settings', href: '/settings' },
  ];

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Clear auth state
      setUser(null);
      clearAuth?.();

      // Clear localStorage
      localStorage.clear();

      // Show success message
      toast.success('Signed out successfully');

      // Redirect to sign in
      navigate('/signin');
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error('Failed to sign out');
    }
  };

  return (
    <>
      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 z-50 lg:hidden">
        <div className="flex items-center justify-between px-4 h-full">
          <Link to="/" className="flex items-center space-x-2">
            <Sparkles className="w-6 h-6 text-emerald-500" />
            <span className="text-xl font-bold bg-gradient-to-r from-emerald-500 to-emerald-700 bg-clip-text text-transparent">
              SF Naturals
            </span>
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="relative">
              <NotificationsDropdown placement="bottom-end" />
            </div>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween' }}
              className="absolute right-0 top-16 bottom-0 w-64 bg-white shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex flex-col h-full">
                <div className="flex-1 p-4 space-y-2">
                  {menuItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setIsOpen(false)}
                      className={clsx(
                        'flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors',
                        location.pathname === item.href
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      )}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  ))}
                </div>

                {/* User Profile and Sign Out */}
                <div className="border-t p-4 space-y-4">
                  <div className="flex items-center space-x-3">
                    <img
                      src={currentUser?.avatar}
                      alt={currentUser?.name}
                      className="w-10 h-10 rounded-full ring-2 ring-emerald-500"
                    />
                    <div>
                      <p className="font-medium text-gray-900">{currentUser?.name}</p>
                      <p className="text-sm text-emerald-600">{currentUser?.role}</p>
                    </div>
                  </div>

                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                    <span className="font-medium">Sign Out</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};